'use client';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, IconButton } from '@mui/material';

import { CategoryChipList, ComponentErrorBoundary, MarketFilters } from '@/components';
import { useAuth, useOpenAuthModal } from '@/hooks';
import { useMixpanel } from '@/hooks/useMixpanel';
import type { Tag } from '@/types';

interface SearchAndFiltersProps {
  searchQuery: string;
  isFilterOpen: boolean;
  selectedTag?: string;
  tags?: Tag[];
  isTagsLoading: boolean;
  onSearchChange: (value: string) => void;
  onFilterToggle: () => void;
  onBookmarksOpen: () => void;
  onTagClick: (slug: string) => void;
  activeCategorySlug?: string;
  sortBy: string;
  frequency: string;
  status: string;
  onSortByChange: (value: string) => void;
  onFrequencyChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  hideSport?: boolean;
  onHideSportChange?: (value: boolean) => void;
  onClearFilters: () => void;
  hideCategoryChips?: boolean;
  hideFilters?: boolean;
  onSearchSubmit?: () => void;
}

export const SearchAndFilters = ({
  isFilterOpen,
  selectedTag,
  tags,
  isTagsLoading,
  onFilterToggle,
  onBookmarksOpen,
  onTagClick,
  activeCategorySlug,
  sortBy,
  frequency,
  status,
  onSortByChange,
  onFrequencyChange,
  onStatusChange,
  hideSport,
  onHideSportChange,
  onClearFilters,
  hideCategoryChips = false,
  hideFilters = false,
  onSearchSubmit,
}: SearchAndFiltersProps) => {
  const openAuthModal = useOpenAuthModal();
  const { isAuthenticated } = useAuth();
  const { trackCategoryTabClicked, trackMarketSearched } = useMixpanel();

  const handleSearchSubmitWrapper = () => {
    trackMarketSearched({ query: searchQuery, results_count: 0 }); // results count unknown at this exact moment
    if (onSearchSubmit) onSearchSubmit();
  };

  const handleTagClickWrapper = (slug: string) => {
    trackCategoryTabClicked({
      tab_name: slug,
      tab_type: 'topic_pill', // or pass from parent
      previous_tab: selectedTag || 'none',
      markets_shown_after: 0,
    });
    onTagClick(slug);
  };

  const handleBookmarksClick = () => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    onBookmarksOpen();
  };

  return (
    <>
      <ComponentErrorBoundary componentName="Search Bar and Filters">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          {!hideCategoryChips && (
            <ComponentErrorBoundary componentName="Category Filters">
              <Box
                sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: 1,
                }}
              >
                <CategoryChipList
                  selectedSlug={selectedTag}
                  tags={tags}
                  isLoading={isTagsLoading}
                  onTagClick={handleTagClickWrapper}
                  activeCategorySlug={activeCategorySlug}
                />
              </Box>
            </ComponentErrorBoundary>
          )}

          {!hideFilters && (
            <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
              <IconButton
                onClick={onFilterToggle}
                sx={{
                  width: 40,
                  height: 40,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                  bgcolor: isFilterOpen ? 'action.selected' : 'background.paper',
                  color: isFilterOpen ? 'primary.main' : 'text.secondary',
                }}
              >
                <TuneIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Box>
          )}
          <IconButton
            onClick={handleBookmarksClick}
            sx={{
              width: 40,
              height: 40,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '10px',
              bgcolor: 'background.paper',
              color: 'text.secondary',
            }}
          >
            <BookmarkBorderIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Box>
      </ComponentErrorBoundary>
      {!hideFilters && isFilterOpen && (
        <MarketFilters
          sortBy={sortBy}
          frequency={frequency}
          status={status}
          onSortByChange={onSortByChange}
          onFrequencyChange={onFrequencyChange}
          onStatusChange={onStatusChange}
          hideSport={hideSport}
          onHideSportChange={onHideSportChange}
          onClearFilters={onClearFilters}
        />
      )}
    </>
  );
};
